import type { RefObject } from "react";
import { useCallback, useEffect, useRef } from "react";

/**
 * Synchronously reports whether the cursor is currently over a given element,
 * respecting z-index / stacking via `document.elementFromPoint`.
 *
 * Why: React hover state tracked through `onMouseEnter`/`onMouseLeave` can go
 * stale when an overlay (modal, popover) covers the element without the cursor
 * moving — the leave handler never fires. Reading CSS `:hover` inside a React
 * effect is also unreliable, since the browser updates `:hover` on the next
 * paint frame, after the effect has already run.
 *
 * How: a passive `pointermove` listener (scoped to the hook's lifecycle) stores
 * the latest cursor position in a ref; the hit-test (`elementFromPoint`) runs
 * only when the returned getter is invoked — no per-frame or per-render work.
 *
 * Usage:
 * ```tsx
 * const ref = useRef<HTMLDivElement>(null);
 * const isCursorOver = useIsCursorOver(ref);
 *
 * <div ref={ref}>...</div>
 *
 * // sync read, safe from inside an effect:
 * if (isCursorOver()) { ... }
 * ```
 */

interface ICursorPosition {
    x: number;
    y: number;
}

export const useIsCursorOver = (ref: RefObject<HTMLElement | null>): (() => boolean) => {
    const cursorPositionRef = useRef<ICursorPosition | null>(null);

    useEffect(() => {
        const handlePointerMove = (event: PointerEvent): void => {
            cursorPositionRef.current = { x: event.clientX, y: event.clientY };
        };

        document.addEventListener("pointermove", handlePointerMove, { passive: true });

        return () => {
            document.removeEventListener("pointermove", handlePointerMove);
        };
    }, []);

    return useCallback((): boolean => {
        const position = cursorPositionRef.current;
        const element = ref.current;

        if (!position || !element) {
            return false;
        }

        const topmost = document.elementFromPoint(position.x, position.y);

        return topmost !== null && element.contains(topmost);
    }, [ref]);
};
